import { useAppDispatch } from "@/app/appStore";
import {
  createStageStatusOptions,
  STAGE_STATUS,
} from "@/features/assignment/lib";
import { displayUUID } from "@/shared";
import {
  lodApi,
  Order,
  StageStatus,
  UpdateOrderStageStatusesRequest,
  useStagesQuery,
  useUpdateOrderStageStatusesMutation,
} from "@/shared/api/lod-api";
import { useAnnotatorData } from "@/shared/hooks/useAnnotatorData";
import { Cross2Icon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  DataList,
  Dialog,
  Flex,
  Heading,
  IconButton,
  SegmentedControl,
  Select,
  Skeleton,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useCopyToClipboard } from "react-use";
import { twMerge } from "tailwind-merge";

type AnnotationStageStatusesFormProps = {
  order: Order;
  readOnly?: boolean;
  onClose?: () => void;
  onSaved?: () => void;
};

export function AnnotationStageStatusesForm({
  order,
  readOnly,
  onClose,
  onSaved,
}: AnnotationStageStatusesFormProps) {
  const dispatch = useAppDispatch();
  const { data: stages, isLoading: isStagesLoading } = useStagesQuery();
  const availableStatuses = stages?.result.statuses;
  const [_, copyToClipboard] = useCopyToClipboard();
  const [stageStatuses, setStageStatuses] = useState<Record<string, string>>(
    {}
  );
  const statusOptions = useMemo(() => {
    const customOrder = [
      STAGE_STATUS.DONE,
      STAGE_STATUS.NOT_STARTED,
      STAGE_STATUS.REJECTED,
    ];

    return createStageStatusOptions(availableStatuses).sort((a, b) => {
      const aIndex = customOrder.indexOf(a.key as StageStatus);
      const bIndex = customOrder.indexOf(b.key as StageStatus);
      return aIndex - bIndex;
    });
  }, [availableStatuses]);

  const [updateOrderStageStatuses, { isLoading }] =
    useUpdateOrderStageStatusesMutation();

  const { annotatorLookup } = useAnnotatorData();

  useEffect(() => {
    if (!order.stage_detail_statuses || !stages?.result.stages) return;

    const gerStatus = (stage: string) =>
      order.stage_detail_statuses?.find((status) => status.stage === stage)
        ?.status || STAGE_STATUS.NOT_STARTED;

    const initialStageStatuses = stages.result.stages.reduce<
      Record<string, string>
    >((acc, stage) => ({ ...acc, [stage.code]: gerStatus(stage.code) }), {});

    setStageStatuses(initialStageStatuses);
  }, [order.stage_detail_statuses, stages]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (readOnly) {
      return;
    }

    const formData = new FormData(e.target as HTMLFormElement);
    const anomalyReport = formData.get("anomaly-report") as string | null;

    const stageStatusesPayload = Object.entries(stageStatuses).map(
      ([stageCode, value]) => {
        const rejectionReason = formData.get(
          `rejection-reason__${stageCode}`
        ) as string | null;

        return {
          stage: stageCode,
          status: value as StageStatus,
          rejection_reason: rejectionReason === "none" ? null : rejectionReason,
        };
      }
    );

    const payload: UpdateOrderStageStatusesRequest = {
      order_id: order.id,
      stage_statuses: stageStatusesPayload,
      anomaly_report: anomalyReport || null,
    };

    try {
      await updateOrderStageStatuses(payload).unwrap();
      toast.success("Order stage statuses updated successfully");
      dispatch(
        lodApi.util.invalidateTags([{ id: order.id, type: "OrderDetails" }])
      );
      onClose?.();
      onSaved?.();
    } catch {
      console.error("Failed to update order stage statuses");
    }
  };

  const onCopy = (text: string | undefined) => {
    if (!text) return;

    copyToClipboard(text);
    toast.success("Copied to clipboard");
  };

  return (
    <>
      <Dialog.Close>
        <IconButton
          className="absolute top-5 right-5"
          variant="ghost"
          color="gray"
          size="1"
        >
          <Cross2Icon width={18} height={18} />
        </IconButton>
      </Dialog.Close>
      <Dialog.Title>Order info</Dialog.Title>
      <form onSubmit={onSubmit}>
        <Flex gap="5" mb="4">
          <DataList.Root size="2" className="gap-x-0 gap-y-2">
            <DataList.Item>
              <DataList.Label minWidth="76px">Order ID:</DataList.Label>
              <DataList.Value>
                <Button
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  highContrast
                  onClick={() => onCopy(order.id)}
                >
                  {displayUUID(order.id) || "-"}
                </Button>
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label minWidth="76px">Created at:</DataList.Label>
              <DataList.Value>
                <Button
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  highContrast
                  onClick={() => onCopy(order.time)}
                >
                  {order.time}
                </Button>
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>
          <DataList.Root size="2" className="gap-x-0 gap-y-2">
            <DataList.Item>
              <DataList.Label minWidth="120px">Source:</DataList.Label>
              <DataList.Value>
                <Button
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  highContrast
                  onClick={() => onCopy(order.order_source)}
                >
                  {order.order_source}
                </Button>
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label minWidth="120px">
                Current assignee:
              </DataList.Label>
              <DataList.Value>
                <Button
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  highContrast
                  onClick={() => onCopy(order.annotator_id)}
                >
                  {order.annotator_id
                    ? annotatorLookup[order.annotator_id] ||
                      displayUUID(order.annotator_id)
                    : "-"}
                </Button>
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>
        </Flex>

        <Box mb="2">
          <Heading size="4" mb="4">
            Stage Statuses
          </Heading>
          {isStagesLoading ? (
            <Flex className="gap-2" gap="2" direction="column" mb="2">
              <Skeleton className="w-full h-[88px]" />
              <Skeleton className="w-full h-[88px]" />
              <Skeleton className="w-full h-[88px]" />
            </Flex>
          ) : (
            stages?.result.stages.map((stage) => (
              <Flex
                gap="2"
                pb="4"
                className="border-b border-[var(--gray-7)]"
                mb="4"
                key={stage.code}
              >
                <Flex flexBasis="50%" direction="column" gap="1">
                  <Text size="2" weight="medium" className="capitalize">
                    {stage.code.split("_").join(" ")}
                  </Text>
                  <SegmentedControl.Root
                    value={stageStatuses[stage.code] || ""}
                    onValueChange={(value) => {
                      if (readOnly) return;

                      setStageStatuses((prev) => ({
                        ...prev,
                        [stage.code]: value,
                      }));
                    }}
                  >
                    {statusOptions.map((status) => (
                      <SegmentedControl.Item
                        className={twMerge([
                          status.key === "done" && "text-[var(--green-a11)]",
                          status.key === "rejected" && "text-[var(--red-a11)]",
                        ])}
                        key={status.key}
                        value={status.key}
                      >
                        {status.value}
                      </SegmentedControl.Item>
                    ))}
                  </SegmentedControl.Root>
                </Flex>
                <Flex flexBasis="50%" direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Rejection Reason
                  </Text>
                  <Select.Root
                    disabled={readOnly}
                    defaultValue={
                      order.stage_detail_statuses?.find(
                        (status) => status.stage === stage.code
                      )?.rejection_reason || "none"
                    }
                    name={`rejection-reason__${stage.code}`}
                  >
                    <Select.Trigger placeholder="-"></Select.Trigger>
                    <Select.Content>
                      <Select.Item value="none">-</Select.Item>
                      {stage.rejection_reasons.map((reason) => (
                        <Select.Item key={reason.code} value={reason.code}>
                          {reason.code}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Flex>
              </Flex>
            ))
          )}
        </Box>
        <Flex direction="column" gap="1">
          <Text size="2" weight="medium">
            Anomaly Report
          </Text>
          <TextArea
            readOnly={readOnly}
            defaultValue={order.anomaly_report || undefined}
            rows={5}
            name="anomaly-report"
          ></TextArea>
        </Flex>

        {!readOnly && (
          <Flex justify="end" gap="2" mt="4">
            <Dialog.Close>
              <Button
                className="w-32"
                variant="soft"
                color="gray"
                type="button"
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              className="w-32"
              color="red"
              type="submit"
              loading={isLoading}
            >
              Save
            </Button>
          </Flex>
        )}
      </form>
    </>
  );
}

type AnnotationStageStatusesDialogProps = {
  order: Order;
  enabled: boolean;
  timeLeft: number;
  onSaved?: () => void;
};

export function AnnotationStageStatusesDialog({
  order,
  enabled,
  timeLeft,
  onSaved,
}: AnnotationStageStatusesDialogProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        {enabled ? (
          <Tooltip
            content={
              <>
                Button stays active for 30 seconds after saving. <br /> To
                reactivate, click "Update" and "Save" again
              </>
            }
          >
            <Dialog.Trigger>
              <Button
                variant="solid"
                highContrast
                color="gray"
                size="2"
                mt="2"
                className="w-full"
              >
                Update Annotation Stage Statuses
              </Button>
            </Dialog.Trigger>
          </Tooltip>
        ) : (
          <Tooltip
            content={
              <>
                Button stays active for 30 seconds after saving. <br /> To
                reactivate, click "Update" and "Save" again
              </>
            }
          >
            <Button
              variant="solid"
              highContrast
              color="gray"
              size="2"
              mt="2"
              disabled={!enabled}
              className="w-full"
            >
              Update Annotation Stage Statuses
            </Button>
          </Tooltip>
        )}

        <Dialog.Content
          maxWidth="702px"
          aria-describedby="stage-statuses-form"
          className="annotation-stage-statuses-dialog"
        >
          <AnnotationStageStatusesForm
            order={order}
            onClose={() => setOpen(false)}
            onSaved={onSaved}
          />
        </Dialog.Content>
      </Dialog.Root>
      {enabled && timeLeft > 0 && (
        <Text size="1" color="gray" align="center">
          Disabled in {timeLeft} seconds
        </Text>
      )}
    </>
  );
}

export function AnnotationStageDetailsDialog({
  order,
  open,
  trigger,
  onOpenChange,
}: {
  order: Order | null;
  open?: boolean;
  trigger?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <Dialog.Trigger>{trigger}</Dialog.Trigger>}
      <Dialog.Content maxWidth="702px" aria-describedby={undefined}>
        {!!order && (
          <AnnotationStageStatusesForm
            order={order}
            readOnly
            onClose={() => onOpenChange?.(false)}
          />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
